import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import ecoLogo from "../../../img/eco-logo.png";

import type { RootState } from "../../../store/store";
import { FREELANCER_LOGIN_PAGE } from "../../auth/routes";
import { FREELANCER_STORE_PAGE } from "../routes";

import { getStoreByIdApi, StorePublic } from "../../store/storeApi";
import { useGoBack } from "../../../hooks/useGoBack";
import {
  listStoreSlotsWithTokenApi,
  createRegistrationWithTokenApi,
  listMyRegistrationsApi,
  getPublicSlotShareLinkInfoApi,
  listSlotsByShareTokenApi,
  MyRegistrationItem,
  StoreSlotWithAvailability,
} from "../../scheduling/schedulingApi";

import "./freelancer-store-slots.css";

function toYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatBrDate(ymd: string): string {
  const [y, m, d] = ymd.split("-");
  return `${d}/${m}/${y}`;
}

function startOfWeekMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function addDaysYmd(ymd: string, daysToAdd: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + daysToAdd);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function buildIsoUtc(dayYmd: string, hhmm: string): string {
  return new Date(`${dayYmd}T${hhmm}:00`).toISOString();
}

function formatTimeFromIso(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function ymdFromIsoOrYmd(s: string): string {
  return s.length >= 10 ? s.slice(0, 10) : s;
}

const FreelancerStoreSlotsPage: React.FC = () => {
  const navigate = useNavigate();
  const goBack = useGoBack(FREELANCER_STORE_PAGE());
  const { storeId } = useParams<{ storeId: string }>();
  const location = useLocation();

  const shareToken = useMemo(() => new URLSearchParams(location.search).get("share"), [location.search]);
  const isShareMode = useMemo(() => Boolean(shareToken), [shareToken]);

  const token = useSelector((state: RootState) => state.freelancerAuth.accessToken);

  const [loadingStore, setLoadingStore] = useState(false);
  const [store, setStore] = useState<StorePublic | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);

  const [weekAnchor, setWeekAnchor] = useState<Date>(() => new Date());
  const weekStart = useMemo(() => startOfWeekMonday(weekAnchor), [weekAnchor]);

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const todayYmd = useMemo(() => toYmd(today), [today]);
  const currentWeekStart = useMemo(() => startOfWeekMonday(today), [today]);

  const canGoPrevWeek = useMemo(() => {
    return weekStart.getTime() > currentWeekStart.getTime();
  }, [weekStart, currentWeekStart]);

  const daysOfWeek = useMemo(() => {
    const labels = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
    return labels.map((label, idx) => {
      const d = addDays(weekStart, idx);
      return { label, date: d, ymd: toYmd(d) };
    });
  }, [weekStart]);

  const [selectedDayYmd, setSelectedDayYmd] = useState<string>(() => toYmd(new Date()));

  const [loadingSlots, setLoadingSlots] = useState(false);
  const [weekSlots, setWeekSlots] = useState<StoreSlotWithAvailability[]>([]);
  const [weekMyRegs, setWeekMyRegs] = useState<MyRegistrationItem[]>([]);
  const [weekMyRegsAll, setWeekMyRegsAll] = useState<MyRegistrationItem[]>([]);

  const [registeringSlotId, setRegisteringSlotId] = useState<string | null>(null);

  const hasAuth = useMemo(() => Boolean(token), [token]);

  const fetchMyStore = useCallback(async () => {
    if (!token) return;
    if (!storeId) return;

    try {
      setLoadingStore(true);
      const found = await getStoreByIdApi(token, storeId);
      setStore(found);
    } catch (err: any) {
      const msg = err?.message ?? "Erro ao carregar loja.";
      toast.error(msg);
    } finally {
      setLoadingStore(false);
    }
  }, [token, storeId, navigate]);

  const fetchPublicStore = useCallback(async () => {
    if (!shareToken) return;

    try {
      setLoadingStore(true);
      setShareError(null);

      const info = await getPublicSlotShareLinkInfoApi(shareToken);

      setStore({
        id: info.store.id,
        nomeFantasia: info.store.nomeFantasia,
        endereco: info.store.endereco ?? null,
      } as any);
    } catch (err: any) {
      const msg = err?.message ?? "Link inválido ou expirado.";
      setShareError(msg);
    } finally {
      setLoadingStore(false);
    }
  }, [shareToken]);

  const fetchWeekSlots = useCallback(async () => {
    if (!storeId) return;

    try {
      setLoadingSlots(true);

      const weekStartYmd = toYmd(weekStart);
      const weekEndYmd = toYmd(addDays(weekStart, 7));

      const from = buildIsoUtc(weekStartYmd, "00:00");
      const to = buildIsoUtc(weekEndYmd, "00:00");

      if (hasAuth && token) {
        const [slots, myRegs] = await Promise.all([
          listStoreSlotsWithTokenApi(storeId, token, {
            from,
            to,
            onlyAvailable: false,
            includeCanceled: false,
          }),
          listMyRegistrationsApi(token, {
            from,
            to,
          }),
        ]);

        setWeekMyRegsAll(myRegs ?? []);
        setWeekMyRegs((myRegs ?? []).filter((r) => r.slot.store.id === storeId));

        const sorted = [...slots].sort((a, b) => a.startAt.localeCompare(b.startAt));
        setWeekSlots(sorted);
        return;
      }

      if (shareToken) {
        const slots = await listSlotsByShareTokenApi(shareToken, {
          from,
          to,
          onlyAvailable: false,
          includeCanceled: false,
        });

        setWeekMyRegsAll([]);
        setWeekMyRegs([]);

        const sorted = [...slots].sort((a, b) => a.startAt.localeCompare(b.startAt));
        setWeekSlots(sorted);
      }
    } catch (err: any) {
      const msg = err?.message ?? "Erro ao listar horários.";
      toast.error(msg);
    } finally {
      setLoadingSlots(false);
    }
  }, [storeId, weekStart, hasAuth, token, shareToken]);

  useEffect(() => {
    if (!storeId) {
      navigate("/freelancer/stores", { replace: true });
      return;
    }

    if (!hasAuth && !isShareMode) {
      toast.error("Você precisa estar logado.");
      navigate(FREELANCER_LOGIN_PAGE(), { replace: true });
      return;
    }

    if (hasAuth) {
      fetchMyStore();
      return;
    }

    if (isShareMode) {
      fetchPublicStore();
    }
  }, [hasAuth, isShareMode, storeId, navigate, fetchMyStore, fetchPublicStore]);

  useEffect(() => {
    if (!store) return;

    const inWeek = daysOfWeek.some((d) => d.ymd === selectedDayYmd);
    const isPast = selectedDayYmd < todayYmd;

    if (!inWeek || isPast) {
      const firstAllowed = daysOfWeek.find((d) => d.ymd >= todayYmd) ?? daysOfWeek[0];
      setSelectedDayYmd(firstAllowed.ymd);
    }
  }, [store, daysOfWeek, selectedDayYmd, todayYmd]);

  useEffect(() => {
    if (!store) return;
    fetchWeekSlots();
  }, [store, fetchWeekSlots]);

  const slotsByDay = useMemo(() => {
    const map: Record<string, StoreSlotWithAvailability[]> = {};
    for (const s of weekSlots) {
      const dayYmd = ymdFromIsoOrYmd(s.day);
      if (!map[dayYmd]) map[dayYmd] = [];
      map[dayYmd].push(s);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => a.startAt.localeCompare(b.startAt));
    }
    return map;
  }, [weekSlots]);

  const selectedDaySlots = useMemo(() => {
    return slotsByDay[selectedDayYmd] ?? [];
  }, [slotsByDay, selectedDayYmd]);

  const myRegisteredSlotIds = useMemo(() => {
    return new Set(weekMyRegs.map((r) => r.slot.id));
  }, [weekMyRegs]);

  const myActiveRegDays = useMemo(() => {
    const s = new Set<string>();
    for (const r of weekMyRegsAll) {
      if (r.status === "PENDENTE" || r.status === "APROVADO") {
        s.add(ymdFromIsoOrYmd(r.day));
      }
    }
    return s;
  }, [weekMyRegsAll]);

  const handleRegister = async (slot: StoreSlotWithAvailability) => {
    if (!token) return;

    const isPastTime = new Date(slot.startAt).getTime() <= Date.now();
    if (slot.isCanceled || slot.isFull || slot.available <= 0 || isPastTime) return;

    try {
      setRegisteringSlotId(slot.id);
      await createRegistrationWithTokenApi(slot.id, token);
      toast.success("Agendamento realizado com sucesso");
      await fetchWeekSlots();
    } catch (err: any) {
      const msg = err?.message ?? "Erro ao se cadastrar no horário.";
      toast.error(msg);
    } finally {
      setRegisteringSlotId(null);
    }
  };

  if (!hasAuth && !isShareMode) return null;

  if (shareError) {
    return (
      <div className="FreelancerSlotsPage">
        <div className="StoreMenuPage">
          <div className="LoginLogoWrap">
            <img className="LoginLogo" src={ecoLogo} alt="ECO System Serviços" />
          </div>

          <div className="StoreMenuContainer">
            <h1 className="StoreMenuTitle">Horários Disponíveis</h1>
            <div className="FreelancerSlotsCard">
              <div className="FreelancerSlotsEmpty">{shareError}</div>
            </div>

            <div className="StoreMenuFooter">
              <button
                type="button"
                className="StoreMenuSecondaryButton"
                onClick={() => navigate("/")}
              >
                Home
              </button>

              <button
                type="button"
                className="StoreMenuLogout"
                onClick={() => navigate(FREELANCER_LOGIN_PAGE())}
              >
                Fazer login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="FreelancerSlotsPage">
      <div className="StoreMenuPage">
        <div className="LoginLogoWrap">
          <img className="LoginLogo" src={ecoLogo} alt="ECO System Serviços" />
        </div>

        <div className="StoreMenuContainer">
          <h1 className="StoreMenuTitle">Horários Disponíveis</h1>

          {loadingStore ? (
            <div className="FreelancerSlotsCard">
              <div className="FreelancerSlotsEmpty">Carregando loja...</div>
            </div>
          ) : !store ? (
            <div className="FreelancerSlotsCard">
              <div className="FreelancerSlotsEmpty">Loja não encontrada.</div>
            </div>
          ) : (
            <>
              <div className="FreelancerSlotsStoreCard">
                <div className="SelectedInfoBox">
                  <div className="SelectedInfoRow">
                    <span className="SelectedInfoLabel">Loja</span>
                    <span className="SelectedInfoValue">{store.nomeFantasia}</span>
                  </div>

                  <div className="SelectedInfoRow">
                    <span className="SelectedInfoLabel">Endereço</span>
                    <span className="SelectedInfoValue">
                      {store.endereco ? store.endereco : "Endereço não informado"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="FreelancerSlotsCard">
                <div className="WeekHeader">
                  <button
                    type="button"
                    className={`PagerButton ${canGoPrevWeek ? "" : "PagerButtonLight"}`}
                    onClick={() => setWeekAnchor((d) => addDays(d, -7))}
                    disabled={!canGoPrevWeek || loadingSlots || registeringSlotId !== null}
                  >
                    Semana anterior
                  </button>

                  <div className="WeekTitle">
                    Semana de <b>{formatBrDate(toYmd(weekStart))}</b>
                  </div>

                  <button
                    type="button"
                    className="PagerButton"
                    onClick={() => setWeekAnchor((d) => addDays(d, 7))}
                    disabled={loadingSlots || registeringSlotId !== null}
                  >
                    Próxima semana
                  </button>
                </div>

                <div className="DayStrip">
                  {daysOfWeek.map((d) => {
                    const active = d.ymd === selectedDayYmd;
                    const isPastDay = d.ymd < todayYmd;

                    const daySlots = slotsByDay[d.ymd] ?? [];
                    const dayAvailable = daySlots.filter((x) => {
                      const pastTime = new Date(x.startAt).getTime() <= Date.now();
                      const alreadyRegistered = myRegisteredSlotIds.has(x.id);
                      return (
                        !x.isCanceled &&
                        !pastTime &&
                        (alreadyRegistered || (x.available > 0 && !x.isFull))
                      );
                    }).length;

                    return (
                      <button
                        key={d.ymd}
                        type="button"
                        className={`DayPill ${
                          !daySlots.length
                            ? "DayPillGray"
                            : myRegisteredSlotIds.size > 0 && daySlots.some((x) => myRegisteredSlotIds.has(x.id))
                            ? "DayPillOrange"
                            : dayAvailable > 0
                            ? "DayPillGreen"
                            : "DayPillRed"
                        } ${active ? "DayPillActive" : ""}`}
                        onClick={() => setSelectedDayYmd(d.ymd)}
                        disabled={isPastDay || loadingSlots || registeringSlotId !== null}
                        title={isPastDay ? "Dia já passou" : undefined}
                      >
                        <div className="DayPillTop">{d.label}</div>
                        <div className="DayPillBottom">{formatBrDate(d.ymd)}</div>
                        <div className="DayPillCount">
                          {dayAvailable}/{daySlots.length} disp.
                        </div>
                      </button>
                    );

                  })}
                </div>

                <div className="Box">
                  <div className="BoxTitle">
                    Horários do dia <b>{formatBrDate(selectedDayYmd)}</b>
                  </div>

                  {loadingSlots ? (
                    <div className="BoxEmpty">Carregando horários...</div>
                  ) : selectedDaySlots.length === 0 ? (
                    <div className="BoxEmpty">Nenhum horário disponível para este dia.</div>
                  ) : (
                    <div className="DraftList">
                      {selectedDaySlots.map((s) => {
                        const endIsNextDay = s.endAt.slice(0, 10) !== s.startAt.slice(0, 10);
                        const endLabel = endIsNextDay
                          ? `${formatTimeFromIso(s.endAt)} (+1)`
                          : formatTimeFromIso(s.endAt);

                        const isPastTime = new Date(s.startAt).getTime() <= Date.now();
                        const alreadyRegistered = myRegisteredSlotIds.has(s.id);
                        const canRegister =
                          !alreadyRegistered &&
                          !s.isCanceled &&
                          !s.isFull &&
                          s.available > 0 &&
                          !isPastTime;
                        const isRegistering = registeringSlotId === s.id;

                        return (
                          <div
                            key={s.id}
                            className={`DraftItem ${s.isCanceled ? "SlotCanceled" : ""}`}
                          >
                            <div className="DraftInfo">
                              <div className="DraftTitle">
                                {formatTimeFromIso(s.startAt)} - {endLabel}
                              </div>
                              <div className="DraftSub">
                                Vagas: {s.available}/{s.totalCapacity} • Ocupado: {s.occupied}
                                {s.isFull ? " • Lotado" : ""}
                                {isPastTime ? " • Horário já passou" : ""}
                              </div>
                            </div>

                            <button
                              type="button"
                              className={`FreelancerRegisterButton ${
                                alreadyRegistered ? "FreelancerRegisterButtonDone" : ""
                              }`}
                              onClick={() => {
                                if (!hasAuth) {
                                  navigate(FREELANCER_LOGIN_PAGE(), { replace: false });
                                  return;
                                }
                                handleRegister(s);
                              }}
                              disabled={
                                hasAuth
                                  ? !canRegister || isRegistering || registeringSlotId !== null
                                  : false
                              }
                            >
                              {hasAuth
                                ? alreadyRegistered
                                  ? "Já confirmado"
                                  : isRegistering
                                  ? "Confirmando..."
                                  : "Confirmar"
                                : "Faça login"}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <div className="StoreMenuFooter">
            <button
              type="button"
              className="StoreMenuSecondaryButton"
              onClick={() => navigate("/")}
            >
              Home
            </button>

            <button
              type="button"
              className="StoreMenuLogout"
              onClick={goBack}
            >
              Voltar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreelancerStoreSlotsPage;
